import { pdf } from "@react-pdf/renderer";
import { createElement } from "react";
import { toPng } from "html-to-image";
import type { Message } from "@/types/chat";
import { PdfDocumentComponent } from "@/components/PdfExport/PdfDocument";
import { colors } from "@/theme/colors";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForChart(container: HTMLElement, maxAttempts = 15): Promise<HTMLElement | null> {
  for (let i = 0; i < maxAttempts; i++) {
    const wrapper = container.querySelector<HTMLElement>(".recharts-wrapper");
    if (wrapper && wrapper.offsetHeight > 50) return wrapper;
    await delay(200);
  }
  return null;
}

async function collectChartImages(
  contentEl: HTMLElement | null,
): Promise<Map<number, string>> {
  const map = new Map<number, string>();
  if (!contentEl) return map;

  await delay(400);

  const chartContainers = contentEl.querySelectorAll<HTMLElement>("[data-chart-index]");

  for (const container of chartContainers) {
    const idx = Number(container.getAttribute("data-chart-index"));
    if (isNaN(idx)) continue;

    const wrapper = await waitForChart(container);
    if (!wrapper) continue;

    try {
      const dataUrl = await toPng(wrapper, {
        pixelRatio: 3,
        backgroundColor: colors.white.light,
        cacheBust: true,
      });
      map.set(idx, dataUrl);
    } catch {
      // chart capture failed, skip
    }
  }

  return map;
}

const LOGO_PATH = "/PIF-logo.png";

async function probeLogo(): Promise<string | null> {
  try {
    const res = await fetch(LOGO_PATH, { method: "HEAD" });
    return res.ok ? LOGO_PATH : null;
  } catch {
    return null;
  }
}

export async function generatePdf(
  message: Message,
  contentRef: React.RefObject<HTMLDivElement | null>,
): Promise<void> {
  const [chartImages, logoSrc] = await Promise.all([
    collectChartImages(contentRef.current),
    probeLogo(),
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const doc = createElement(PdfDocumentComponent, { message, chartImages, logoSrc }) as any;
  const blob = await pdf(doc).toBlob();

  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const filename = `Report-${dd}-${mm}-${yyyy}.pdf`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
